/**
 * useVapi.ts — VAPI voice call hook for Orbi
 * =============================================
 *
 * Manages the full lifecycle of a VAPI voice call:
 *   - Connection state (disconnected → connecting → connected → error)
 *   - Agent state (idle → listening → processing → speaking)
 *   - Audio level monitoring for orb reactivity
 *   - Transcript logging (last 3 messages)
 *   - Dynamic tool loading from CustomerContext skills
 *   - show_card tool definition (client-side, displays data cards in UI)
 *   - Farewell detection with auto-end after 3s silence
 *   - Session-end reporting to trigger Haiku self-improvement
 *
 * CHANGES IN THIS VERSION:
 *   - Added SHOW_CARD_TOOL constant (client-side tool definition)
 *   - Injected show_card into the tools array passed to VAPI
 *   - Wired show_card tool-calls handler — AI can now trigger cards directly
 *   - useVapi accepts optional onShowCard callback for card display
 *   - Transcriber language set to 'multi' (supports Spanish/Afrikaans switch)
 *   - Model temperature tuned to 0.45 (warmer than baseline 0.3 but tight
 *     enough that proactive card rules still fire reliably)
 *
 * APRIL 23 UPDATES (PPA-007 + Phase 0.5):
 *   - card_type enum extended with three PPA-007 payment cards:
 *       payment_link_sent, payment_confirmed, service_reactivating
 *   - CARD_TYPE_ALIASES normalises mis-fired card_type values before they
 *     reach VoiceOverlay. Earlier prompt revisions taught Orbi to fire
 *     card_type "billing_alert" (which is an intent, not a card type);
 *     we collapse it to "account" so the call doesn't silently fail.
 *
 * File: src/hooks/useVapi.ts
 * Last updated: April 23, 2026
 */
import { useRef, useState, useCallback, useEffect } from 'react';
import Vapi from '@vapi-ai/web';
import { ConnectionState, AgentState, LogMessage, CustomerContext, OrbiMode } from '../types';
import { buildSystemPromptAsync, isUnknownCaller } from '../lib/contextFetcher';
import { OPERATOR_SPEECH_RULES, buildDockKeyterms } from '../lib/speech';

/* ── Voice: persona_voices is the single source of truth (architectural_truth
   rule 42). The orb resolves the scoped persona's voice from persona_voices and
   passes it here (context.persona_voice). This consolidated default mirrors the
   live persona_voices default row on Teglo (oqdglnryqqinqjgyxvaz), verified
   2026-06-24: every active persona maps to 6aDn1KB0hjpdcocrUkmq / eleven_flash_v2_5.
   It is the fallback so the talk-mode voice is NEVER the old static voiceId, even
   when the live read is unavailable. */
const CONSOLIDATED_VOICE = {
  provider: '11labs' as const,
  voiceId: '6aDn1KB0hjpdcocrUkmq',
  model: 'eleven_flash_v2_5',
  stability: 0.6,
  similarityBoost: 0.85,
  style: 0.45,
  useSpeakerBoost: true,
  speed: 1,
};
function resolveVapiVoice(personaVoice: any) {
  if (personaVoice && personaVoice.voiceId) {
    return {
      provider: '11labs' as const,
      voiceId: personaVoice.voiceId,
      model: personaVoice.model || CONSOLIDATED_VOICE.model,
      stability: personaVoice.stability ?? CONSOLIDATED_VOICE.stability,
      similarityBoost: personaVoice.similarityBoost ?? CONSOLIDATED_VOICE.similarityBoost,
      style: personaVoice.style ?? CONSOLIDATED_VOICE.style,
      useSpeakerBoost: personaVoice.useSpeakerBoost ?? CONSOLIDATED_VOICE.useSpeakerBoost,
      speed: personaVoice.speed ?? CONSOLIDATED_VOICE.speed,
    };
  }
  return CONSOLIDATED_VOICE;
}
/* ── Environment variables ── */
/* TEGLO-DASHBOARD SHIM: also read from window.__ORBI_ENV__ so the committed
   bundle can be re-targeted without a rebuild. The host page (index.html in
   teglo-dashboard) is static and has no build step, so build-time inlining
   of VITE_ vars only works if Vercel runs vite build during deploy. The
   window shim is the runtime fallback for any var the build missed. */
const __ORBI_ENV__: any =
  (typeof window !== 'undefined' && (window as any).__ORBI_ENV__) || {};
const VAPI_PUBLIC_KEY = __ORBI_ENV__.VAPI_PUBLIC_KEY || import.meta.env.VITE_VAPI_PUBLIC_KEY;

/* ══════════════════════════════════════════════════════════
   NO-PHONE VOICE SIM (test rig transport, JARVIS NAV gate battery)
   A drop-in stand-in for the Vapi transport so the no-phone battery and the
   recording rig can drive a REAL "live call" through the REAL event handlers
   below (transcripts, tool-calls, state machine) with zero mic, zero WebRTC,
   zero Vapi account. ONLY active when the harness sets
   window.__ORBI_ENV__.ORBI_VOICE_SIM === true BEFORE the bundle loads; no
   production page sets it, so prod behaviour is byte-identical. The instance
   is published as window.__ORBI_VAPI_SIM__ so the harness can emit events
   ({type:'transcript'|'tool-calls'}) and inspect what the agent was sent.
   ══════════════════════════════════════════════════════════ */
const VOICE_SIM: boolean = __ORBI_ENV__.ORBI_VOICE_SIM === true;
class SimVapiTransport {
  private handlers: Record<string, Array<(...args: any[]) => void>> = {};
  /** every add-message the app sent back to the "agent" (tool results etc.) */
  sent: any[] = [];
  /** the last start() options (system prompt + tools), so the battery can
   *  assert exactly what the agent was armed with at call start. */
  lastStart: any = null;
  on(ev: string, fn: (...args: any[]) => void) { (this.handlers[ev] = this.handlers[ev] || []).push(fn); }
  emit(ev: string, ...args: any[]) { (this.handlers[ev] || []).forEach((f) => { try { f(...args); } catch { /* handler threw */ } }); }
  async start(opts: any) { this.lastStart = opts; this.emit('call-start'); }
  stop() { this.emit('call-end'); }
  send(m: any) { this.sent.push(m); }
}
/* DROP-IN (Phase 3 binding 4): derive from the configured project, never a
   hardcoded one - a dock on another vertical must not post session ends to
   Teglo. Empty config leaves the URL empty; the sender already no-ops then. */
const SESSION_END_URL = __ORBI_ENV__.SESSION_END_URL || import.meta.env.VITE_SESSION_END_URL
  || (__ORBI_ENV__.SUPABASE_URL ? __ORBI_ENV__.SUPABASE_URL + '/functions/v1/orbi-session-end' : '');
/* ══════════════════════════════════════════════════════════
   FAREWELL AUTO-END
   Orbi listens for unambiguous farewell phrases in assistant
   messages. If the user doesn't respond within ~3s after a
   farewell, the call ends automatically.

   APRIL 23 FIX: removed ambiguous phrases that were firing
   false positives mid-call — e.g. "Is there anything else
   I can help with?" after send_payment_link auto-narrated
   was killing the call before Orbi could properly say
   goodbye. Only keep phrases that unambiguously close a
   call: explicit "goodbye" forms, "have a good/great/
   lovely [day]", "take care", "cheers for now".
   ══════════════════════════════════════════════════════════ */
const FAREWELL_PATTERNS = [
  'goodbye', 'good bye', 'bye for now', 'bye-bye', 'bye bye',
  'take care', 'have a good', 'have a great', 'have a lovely',
  'cheers for now',
];
const FAREWELL_SILENCE_MS = 3000;
const ESCALATION_INTENT_PHRASES = [
  'speak to a human', 'talk to a person', 'real person',
  'billing team', 'someone else', 'transfer',
  'speak to someone', 'talk to someone', 'human agent',
  'actual person', 'real human',
];
/* ══════════════════════════════════════════════════════════
   SHOW_CARD ALIAS MAP (Phase 0.5)
   Earlier prompt revisions taught Orbi to fire card_type
   "billing_alert" (actually an intent, not a card type). Those
   calls were silently rejected. Rather than re-training the
   model mid-demo, we normalise here:
     billing_alert → account
   Keep "generic" as-is — it's a real card type with full UI
   support via GenericCard in VoiceOverlay.
   ══════════════════════════════════════════════════════════ */
const CARD_TYPE_ALIASES: Record<string, string> = {
  billing_alert: 'account',
};
/* ══════════════════════════════════════════════════════════
   SHOW_CARD TOOL DEFINITION
   Client-side tool that tells the UI to display a data card.
   When VAPI's AI model decides to show contextual info, it
   calls this tool with a card_type parameter.
   Card types (from ORBI-CARD-SPEC-REAL.md):
     diagnosis             — Orbi's AI assessment
     account               — account & billing status
     area_pulse            — area-wide issue overview
     history               — past call history & insights
     ticket                — open support ticket details
     generic               — AI-authored content with intent + payload
     payment_link_sent     — PPA-007: payment link dispatched
     payment_confirmed     — PPA-007: payment received, service restoring
     service_reactivating  — PPA-007: service restoration in progress
   NOTE: Handler is wired — when VAPI fires a tool-calls event
   for show_card, the onShowCard callback is invoked and
   VoiceOverlay displays the card immediately.
   ══════════════════════════════════════════════════════════ */
const SHOW_CARD_TOOL = {
  type: 'function' as const,
  function: {
    name: 'show_card',
    description:
      'Display a contextual data card to the user during the conversation. ' +
      'Use typed cards (diagnosis, account, area_pulse, history, ticket, ' +
      'payment_link_sent, payment_confirmed, service_reactivating) for standard info, ' +
      'or use card_type "generic" with a custom payload to show any content — like speed test ' +
      'results, reboot status, or custom summaries. Only show a card when directly relevant.',
    parameters: {
      type: 'object' as const,
      properties: {
        card_type: {
          type: 'string' as const,
          enum: [
            'diagnosis',
            'account',
            'area_pulse',
            'history',
            'ticket',
            'generic',
            'payment_link_sent',
            'payment_confirmed',
            'service_reactivating',
          ],
          description:
            'Card type. Use "generic" for custom content with intent/heading/metric/description/badges/cta. The intent field resolves the icon automatically.',
        },
        intent: {
          type: 'string' as const,
          enum: [
            'connection_status',
            'speed_result',
            'ticket_created',
            'account_status',
            'billing_alert',
            'area_outage',
            'escalation_confirmed',
            'diagnosis',
            'speed_healthy',
          ],
          description: 'Pick the intent that best describes what you are showing. The system will resolve the correct icon and label for this vertical.',
        },
        heading: {
          type: 'string' as const,
          description: 'Bold heading text for generic cards.',
        },
        metric: {
          type: 'string' as const,
          description: 'Large prominent number or status for generic cards, e.g. "87 Mbps" or "Success".',
        },
        description: {
          type: 'string' as const,
          description: 'Supporting text below the metric for generic cards.',
        },
        badges: {
          type: 'array' as const,
          description: 'Status badges for generic cards. Each has text and variant (ok/warn/danger/info/muted).',
          items: {
            type: 'object' as const,
            properties: {
              text: { type: 'string' as const },
              variant: { type: 'string' as const, enum: ['ok', 'warn', 'danger', 'info', 'muted'] },
            },
          },
        },
        cta: {
          type: 'object' as const,
          description: 'Call-to-action button for generic cards. Action is the skill name to trigger.',
          properties: {
            label: { type: 'string' as const },
            action: { type: 'string' as const },
          },
        },
        amount: {
          type: 'number' as const,
          description: 'For payment_link_sent / payment_confirmed cards: the numeric amount (e.g. 89). Pass this whenever you have a known price — it is required for acquisition flows where billing_context is empty.',
        },
        currency: {
          type: 'string' as const,
          description: 'For payment_link_sent / payment_confirmed cards: ISO currency code (e.g. "USD", "ZAR"). Always pass this alongside amount so the visual symbol matches what you spoke.',
        },
      },
      required: ['card_type'],
    },
  },
};
/* ══════════════════════════════════════════════════════════
   LEAD_CAPTURE_V1 TOOL DEFINITION (client-side, DORMANT)
   SEC-1 step d (2026-10-21): nothing sets context.lead_routing any
   more. The browser read of the ORA-CORE lead routing table is gone,
   with the key and the webhook URL it carried, so this tool is never
   added to a call's tools array and the handler below never runs.
   lead_capture_v1 on a live call is composed server-side by
   orbi-context-fetch from vertical_persona_skills and lands on
   orbi-lead-capture-proxy. The branch stays until a dispatch names
   its deletion (L5).

   Tool exposure is gated on context.lead_routing.
   ══════════════════════════════════════════════════════════ */
const LEAD_CAPTURE_TOOL = {
  type: 'function' as const,
  function: {
    name: 'lead_capture_v1',
    description:
      'Capture a lead during a voice session. Call this after you have asked ' +
      'the caller for their full name, phone, email, and what they are looking ' +
      'for help with. Fire exactly once per call. The team will follow up by email.',
    parameters: {
      type: 'object' as const,
      properties: {
        customer_name: {
          type: 'string' as const,
          description: 'Full name as the caller said it. Empty string if declined.',
        },
        phone: {
          type: 'string' as const,
          description: 'Best contact number. Empty string if declined.',
        },
        email: {
          type: 'string' as const,
          description: 'Contact email. Empty string if declined.',
        },
        need: {
          type: 'string' as const,
          description: 'Free-text description of what they want help with.',
        },
      },
      required: ['customer_name', 'phone', 'email', 'need'],
    },
  },
};
/* ══════════════════════════════════════════════════════════
   SHOW_BLOCKS TOOL DEFINITION (JARVIS-PLATFORM-ORB Phase 4)
   Voice parity for the generative UI: the OPERATOR-mode agent can spin up
   the same registry blocks the typed ask path renders (subscriber_card,
   trust_donut, metric_chips, options, table). The payload is JSON only and
   is validated by the SAME client registry (validateUiBlocks) before
   anything paints - unknown types, fields and actions are dropped, so this
   tool cannot render markup. Exposed ONLY in operator mode and ONLY when
   the ORBI_GENERATIVE_UI flag is on (resolved at call start). Rule 51: a
   call action still runs the confirm-on-tap dialer + the server allowlist
   gate; a navigate action defers until the call ends so a tap never drops
   the live call.
   ══════════════════════════════════════════════════════════ */
const SHOW_BLOCKS_TOOL = {
  type: 'function' as const,
  function: {
    name: 'show_blocks',
    description:
      'Display rich data blocks to the operator while you speak. Allowed block types: ' +
      'subscriber_card {props:{heading, metric?, description?, badges?:[{text,variant:ok|warn|danger|muted}], account_number?}}, ' +
      'trust_donut {props:{heading?, confidence (0..1 ONLY when you have a real grounded score, else omit the block), interactions?, last_contact?}}, ' +
      'metric_chips {props:{heading?, chips:[{label,value,variant?}]}}, ' +
      'options {props:{heading?}} with actions as the choices, ' +
      'table {props:{heading?, columns (max 4), rows (max 6)}}. ' +
      'Each block may carry at most 2 actions {label, action: call|ask|navigate, params}: call params {account_number, name?, reason?} places a gated outbound call on the operator\'s tap (phrase it "On your go, I can call {name}"); ask params {prompt}; navigate params {section} opens a screen AFTER this call ends. ' +
      'Use only values you actually know from the conversation or your context. At most 2 blocks per call; do not re-show the same block.',
    parameters: {
      type: 'object' as const,
      properties: {
        blocks: {
          type: 'array' as const,
          description: 'The registry blocks to display, as plain JSON objects.',
          items: { type: 'object' as const },
        },
      },
      required: ['blocks'],
    },
  },
};

/* Subject-by-voice (Eri round 2 item 6): the OPERATOR agent resolves a spoken
   subscriber name into the real record without the operator clicking anything.
   The handler runs the SAME lens-scoped search as the typed "call <name>" path,
   primes the dock subject on a unique match, and returns the FULL live detail
   (status, billing, plan, connection, trust confidence, open tickets) so the
   agent discusses THAT subscriber from real rows. Operator mode only. */
const LOOKUP_SUBSCRIBER_TOOL = {
  type: 'function' as const,
  function: {
    name: 'lookup_subscriber',
    description:
      'Resolve a subscriber the operator NAMES out loud (e.g. "tell me about Robin Example", "pull up account LB-66248F9C") into their live record, and prime them as the open subject. ' +
      'Call this the moment the operator names a subscriber you do not already have loaded. ' +
      'Returns the full live record: status, billing, plan, connection, trust confidence (0..1, real when present), recurring issues, risk flags, open ticket count. ' +
      'If more than one subscriber matches, you get the candidate list; read the names back and ask which one. If none match, say so plainly; never invent a subscriber.',
    parameters: {
      type: 'object' as const,
      properties: {
        name: { type: 'string' as const, description: 'The subscriber name or account number the operator said.' },
      },
      required: ['name'],
    },
  },
};

/* ══════════════════════════════════════════════════════════
   JARVIS_NAVIGATE TOOL DEFINITION (JARVIS NAV, one brain)
   The operator-mode agent routes SPOKEN commands through the SAME
   deterministic classifier the typed panel uses (orbi-operator-ask with
   jarvis_nav + classify_only): config-driven intents, tenant-scoped entity
   resolution, evidence-gated answers, identical cards. Navigation executes
   IN PLACE through the workspace layer, so the live call never drops.
   Exposed ONLY in operator mode and ONLY when ORBI_JARVIS_NAV was on at
   call start (the dock stamps the context) - kill switch at both ends.
   ══════════════════════════════════════════════════════════ */
const JARVIS_NAV_TOOL = {
  type: 'function' as const,
  function: {
    name: 'jarvis_navigate',
    description:
      'Route the operator\'s spoken command through the platform navigator - the SAME deterministic, config-driven classifier the typed panel uses. ' +
      'Call this FIRST, with the operator\'s words as spoken, whenever they ask to open, show, or go to a screen, a subscriber, an account number, or a building ' +
      '("take me to customer 55102", "open billing", "show me the network", "what\'s down"), or ask a factual question that names an account number. ' +
      'A navigation match opens the screen IN PLACE while this call continues - never tell the operator you cannot navigate mid-call, and never promise to open something after the call. ' +
      'The result tells you what happened: navigated:true means the screen is opening now (say so briefly); an answer means it is already on the panel (speak its gist); matched:false means no deterministic match - answer from your own context and tools instead. ' +
      'SPEECH GATE: your spoken outcome comes ONLY from this result, never from what you believe your capabilities are. navigated:true = the screen DID move - confirm the move; saying you cannot navigate is forbidden. navigated:false = it did NOT move - speak the reason field the result carries (refused, deferred, or unavailable). Do not announce success or inability before the result arrives.',
    parameters: {
      type: 'object' as const,
      properties: {
        utterance: { type: 'string' as const, description: 'The operator\'s command or question, as spoken.' },
      },
      required: ['utterance'],
    },
  },
};

/* ══════════════════════════════════════════════════════════
   CAPABILITY TOOLS (JARVIS SITUATIONAL AWARENESS)
   The jarvis_capabilities config maps a capability to tool NAMES; only names
   with a definition HERE ever reach the call, so config can surface but never
   invent a tool. Exposed ONLY in operator mode, only when the dock stamped
   _operator.capability_tools at call start (which it does only with
   ORBI_JARVIS_NAV + ORBI_GENERATIVE_UI both on).

   show_resolve_preview / fire_mass_resolve are the voice face of the SAME
   governed outage-mass-resolve endpoint the network page's confirm modal
   uses: preview is zero-write; fire re-computes the selection server-side
   under the dedup + test-mode allowlist + quiet-hour gates, and the dock
   additionally requires the operator's explicit on-screen confirm before it
   relays a fire. The agent can OFFER and PREVIEW freely; it can never place
   a sweep on its own say-so.
   ══════════════════════════════════════════════════════════ */
const SHOW_RESOLVE_PREVIEW_TOOL = {
  type: 'function' as const,
  function: {
    name: 'show_resolve_preview',
    description:
      'Preview the mass-resolve sweep for the active outage event on the network view: exactly which affected customers would get the one honest service-notice call, who is excluded and why (suspended, never provisioned, no phone, already called, bulk/MDU collapsed to one property-manager note), and whether test mode is on. ' +
      'Zero writes, nothing dials. Call this when the operator asks for help resolving a mass outage, or before offering fire_mass_resolve. ' +
      'Only works on the network view with an active outage event on screen; the result says so honestly otherwise.',
    parameters: { type: 'object' as const, properties: {} },
  },
};
const FIRE_MASS_RESOLVE_TOOL = {
  type: 'function' as const,
  function: {
    name: 'fire_mass_resolve',
    description:
      'Fire the governed mass-resolve sweep for the active outage event: one service-notice call per included customer, computed and re-checked server-side. ' +
      'The operator must confirm on screen before anything is relayed - calling this tool OPENS that confirm; it cannot dial by itself. ' +
      'Always run show_resolve_preview and tell the operator the numbers first. In test mode only staff allowlist numbers can ring.',
    parameters: { type: 'object' as const, properties: {} },
  },
};
/** name -> definition. The ONLY tools config names can summon. */
const CAPABILITY_TOOL_DEFS: Record<string, any> = {
  show_resolve_preview: SHOW_RESOLVE_PREVIEW_TOOL,
  fire_mass_resolve: FIRE_MASS_RESOLVE_TOOL,
};

/** Client-side tool: endCall — lets the AI gracefully hang up.
 *  The farewell line is spoken by Orbi (per the ## ENDING block in the
 *  system prompt). This tool only disconnects — no auto-narration, or
 *  the customer hears the farewell twice. */
const END_CALL_TOOL = {
  type: 'function' as const,
  function: {
    name: 'endCall',
    description: 'Disconnect the call. You must have already spoken the single farewell line from the ## ENDING block. Do not call this tool before the farewell, and never call it twice.',
    parameters: { type: 'object' as const, properties: {} },
  },
};
/* ══════════════════════════════════════════════════════════
   TOOL NARRATION MESSAGES
   VAPI speaks these automatically when the matching tool fires,
   removing the dependency on the LLM following prompt instructions.
   ══════════════════════════════════════════════════════════ */
const TOOL_MESSAGES: Record<string, { requestStartMessage: string }> = {
  escalate_to_agent: { requestStartMessage: "I'm flagging this for our support team right now — someone will call you back shortly." },
  diagnose_connection: { requestStartMessage: "Let me run a quick diagnostic on your line now." },
  speed_test: { requestStartMessage: "I'm running a speed test on your connection right now." },
  log_ticket: { requestStartMessage: "I'm logging a support ticket for you now." },
  send_payment_link: { requestStartMessage: "I'm sending you a secure payment link now." },
  address_service_check: { requestStartMessage: "Let me check coverage at that address." },
  send_order_sms: { requestStartMessage: "Sending the details to your phone." },
  send_order_email: { requestStartMessage: "Sending your confirmation email now." },
  capture_lead: { requestStartMessage: "Saving your details now." },
  lead_capture_v1: { requestStartMessage: "Saving your details now." },
};
/* ══════════════════════════════════════════════════════════
   BUILD TOOLS FROM CONTEXT
   Reads skills from the CustomerContext (returned by the
   context-fetch edge function) and builds VAPI tool objects.
   Now also injects the show_card client-side tool.
   ══════════════════════════════════════════════════════════ */
export function buildToolsFromContext(context: CustomerContext) {
  // Dynamic tools from context skills (server-side, with webhook URLs)
  const skillTools = (!context.skills || context.skills.length === 0)
    ? []
    : context.skills.map(skill => {
        const msgs = TOOL_MESSAGES[skill.name];
        return {
          type: 'function' as const,
          function: {
            name: skill.name,
            description: skill.description,
            parameters: {
              type: 'object' as const,
              properties: (skill.parameters as any)?.properties || {},
              ...((skill.parameters as any)?.required
                ? { required: (skill.parameters as any).required }
                : {}),
            },
          },
          ...(msgs ? {
            messages: [
              { type: 'request-start' as const, content: msgs.requestStartMessage },
            ]
          } : {}),
          server: { url: skill.webhook_url },
        };
      });
  // Always include the show_card client-side tool
  const tools: any[] = [...skillTools, SHOW_CARD_TOOL, END_CALL_TOOL];
  // Phase 4 voice parity: the generative-UI block tool, OPERATOR mode only and
  // only when the flag was on at call start (the caller stamps the context).
  if ((context as any)._operator?.generative_ui === true) {
    tools.push(SHOW_BLOCKS_TOOL);
  }
  // Subject-by-voice (round 2 item 6): operator mode always carries the
  // lookup tool - resolving a spoken name is core copilot behaviour, not a
  // generative-UI extra.
  if ((context as any)._operator) {
    tools.push(LOOKUP_SUBSCRIBER_TOOL);
  }
  // JARVIS NAV one-brain tool: operator mode only, and only when the flag was
  // on at call start (the dock stamps the context, same as generative_ui).
  // BARGE/SNAG FIX (round 5): give the nav tool a CONTROLLED request-start line.
  // Without one, Vapi free-forms a filler while the (server) classifier round-
  // trips, and the model hedges with "hit a snag / connectivity issue" - the
  // exact spoken failure the operator heard WHILE the screen actually moved.
  // A neutral warm filler here occupies that gap with something honest that
  // claims neither success nor failure; the tool RESULT then drives the real
  // confirmation. request-start is Vapi-spoken, never triggered for async tools.
  if ((context as any)._operator?.jarvis_nav === true) {
    tools.push({
      ...JARVIS_NAV_TOOL,
      messages: [{ type: 'request-start' as const, content: 'Sure, one sec.' }],
    });
  }
  // JARVIS SITUATIONAL AWARENESS capability tools: the dock stamps the tool
  // NAMES its tenant-scoped config resolved (only with both flags on); only
  // names with a definition here are registered. The whole tenant catalogue
  // registers at call start because tools are fixed for the call's life and
  // the operator can navigate to the capability's screen mid-call; execution
  // is still gated on the live context at fire time.
  const capabilitySeen = new Set(tools.map((t) => t.function?.name));
  // Same request-start remedy for the resolve tools: show_resolve_preview
  // navigates to the Resolve board (a round-trip), fire runs the governed
  // sweep - both need a controlled warm filler so the model never fills the
  // gap with a snag line.
  const CAPABILITY_TOOL_FILLERS: Record<string, string> = {
    show_resolve_preview: "Sure, let me pull up who we'd call.",
    fire_mass_resolve: 'One sec.',
  };
  const armCapabilityTool = (def: any) => {
    if (def && !capabilitySeen.has(def.function.name)) {
      const filler = CAPABILITY_TOOL_FILLERS[def.function.name];
      tools.push(filler ? { ...def, messages: [{ type: 'request-start' as const, content: filler }] } : def);
      capabilitySeen.add(def.function.name);
    }
  };
  const capabilityToolNames: unknown = (context as any)._operator?.capability_tools;
  if (Array.isArray(capabilityToolNames)) {
    for (const nm of capabilityToolNames) armCapabilityTool(CAPABILITY_TOOL_DEFS[String(nm)]);
  }
  // JARVIS RESOLVE: a pill-primed session on the network page arms the pair
  // from PAGE truth (resolve bridge present + active outage event), so the
  // Resolve control works even for a tenant with no capability rows yet.
  // Same definitions as the catalogue path; the seen-set keeps each tool once.
  if ((context as any)._operator?.mass_resolve === true) {
    armCapabilityTool(SHOW_RESOLVE_PREVIEW_TOOL);
    armCapabilityTool(FIRE_MASS_RESOLVE_TOOL);
  }
  // Add lead_capture_v1 only when a lead routing row resolved for this
  // session. Exposing the tool without routing config would let Orbi
  // call it and silently fail at POST time.
  if (context.lead_routing) {
    const msgs = TOOL_MESSAGES['lead_capture_v1'];
    tools.push({
      ...LEAD_CAPTURE_TOOL,
      ...(msgs ? {
        messages: [
          { type: 'request-start' as const, content: msgs.requestStartMessage },
        ],
      } : {}),
    });
  }
  return tools;
}
/* ══════════════════════════════════════════════════════════
   SESSION END REPORTING
   Fire-and-forget POST to the session-end edge function.
   Triggers Haiku self-improvement pipeline.
   ══════════════════════════════════════════════════════════ */
async function reportSessionEnd(
  context: CustomerContext,
  mode: OrbiMode,
  allMessages: Array<{ role: string; text: string }>,
  actionsTaken: Array<{ skill: string; result?: string; success: boolean }>,
  startTime: number
) {
  try {
    await fetch(SESSION_END_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        account_number: context.account_number,
        vertical_id: null,
        persona_key: context.persona?.persona_key || (mode === 'customer' ? 'customer_support' : 'network_engineer'),
        mode,
        actions_taken: actionsTaken,
        messages: allMessages,
        duration_seconds: Math.round((Date.now() - startTime) / 1000),
      }),
    });
    console.log('[Orbi] Session end reported → Haiku processing');
  } catch (err) {
    console.error('[Orbi] Failed to report session end:', err);
  }
}
/* Human-readable reason for a failed call start, surfaced next to the mic
 * control instead of the button just silently coming back with no
 * explanation. Recognises a denied/unavailable microphone specifically
 * (the join failure a browser reports as NotAllowedError/PermissionDeniedError
 * or NotFoundError) since that is the overwhelmingly common cause; anything
 * else gets an honest generic line rather than a guess. */
function describeMicError(error: any): string {
  const name: string = error?.error?.name || error?.error?.error?.name || '';
  const message: string = String(error?.error?.message || error?.error?.errorMsg || error?.message || '').toLowerCase();
  if (/notallowederror|permissiondenied/i.test(name) || message.includes('permission') || message.includes('denied')) {
    return 'Microphone access was denied. Allow microphone access for this site and try again.';
  }
  if (/notfounderror/i.test(name) || message.includes('no microphone') || message.includes('device not found')) {
    return 'No microphone was found. Connect a microphone and try again.';
  }
  return "Couldn't start the voice call. Check your microphone and connection, then try again.";
}
/* ══════════════════════════════════════════════════════════
   VAPI HOOK
   Optional onShowCard callback — called when AI triggers
   a show_card tool call. Pass from VoiceOverlay to display
   cards in response to AI decisions (not just rule triggers).
   ══════════════════════════════════════════════════════════ */
export function useVapi(
  onShowCard?: (cardType: string, payload?: any) => void,
  /** Phase 4: the agent spun up registry blocks. The callback validates them
   *  through the client registry and returns the surviving types (empty when
   *  everything was dropped), so the tool result tells the agent the truth. */
  onShowBlocks?: (rawBlocks: unknown) => { types: string[] },
  /** Round 2 item 6 (subject-by-voice): resolve a spoken subscriber name via
   *  the lens-scoped search, prime the dock subject on a unique match, and
   *  return the full live detail (or candidates / not-found) for the agent. */
  onLookupSubscriber?: (name: string) => Promise<any>,
  /** JARVIS NAV one-brain: route a spoken command through the deterministic
   *  classifier (the typed path's brain). Resolves to the structured result
   *  the agent speaks from: {matched, navigated?, route?, answer?, note}. */
  onJarvisCommand?: (utterance: string) => Promise<any>,
  /** JARVIS SITUATIONAL AWARENESS: zero-write mass-resolve preview for the
   *  active outage event on screen. Resolves to the structured summary the
   *  agent speaks from (or an honest not-available result). */
  onResolvePreview?: () => Promise<any>,
  /** Fire the governed sweep - the dock requires the operator's explicit
   *  on-screen confirm before relaying; resolves to what actually happened. */
  onResolveFire?: () => Promise<any>
) {
  const vapiRef = useRef<Vapi | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.DISCONNECTED);
  const [agentState, setAgentState] = useState<AgentState>('idle');
  const [audioLevel, setAudioLevel] = useState(0);
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [toolCallCount, setToolCallCount] = useState(0);
  /* JARVIS MIC DEFECT FIX: the ask-mode mic button (VoiceOverlayPanel) unmounts
   * the instant connectionState leaves DISCONNECTED (voiceActive = isConnected ||
   * isConnecting), swapping to the voice hero. ConnectionState.ERROR was never
   * part of that check, so a failed start (mic permission denied, Vapi/Daily
   * join error) silently fell back to the plain ask panel with zero explanation
   * — the operator only saw the mic button vanish and the panel look unchanged,
   * reading as "clicked mic, button disappeared, nothing started". micError
   * carries a human reason alongside ERROR so the panel can show it instead of
   * failing silently (fail open: the control stays, with a visible reason). */
  const [micError, setMicError] = useState<string | null>(null);
  /* Fail-open safety net: if the browser's mic-permission prompt is left
   * unanswered (or the SDK hangs for any other reason) connectionState can sit
   * at CONNECTING forever with no 'error' event ever firing — the hero panel
   * (no mic button) never resolves. This forces a visible failure instead of an
   * indefinite hang. Cleared on any real resolution (call-start, error, stop). */
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearConnectTimeout = useCallback(() => {
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
  }, []);
  /* ── Farewell auto-end timer ── */
  const farewellTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recentToolCalls = useRef<Map<string, number>>(new Map());
  const lastUserTranscript = useRef<string>('');
  const clearFarewellTimer = useCallback(() => {
    if (farewellTimer.current) {
      clearTimeout(farewellTimer.current);
      farewellTimer.current = null;
    }
  }, []);
  /* ── Session tracking ref (mutable, no re-renders) ── */
  const sessionRef = useRef<{
    context: CustomerContext | null;
    mode: OrbiMode;
    allMessages: Array<{ role: string; text: string }>;
    actionsTaken: Array<{ skill: string; result?: string; success: boolean }>;
    startTime: number;
    /** UUID minted on startCall. Used as session_id in the canonical
     *  lead_capture_v1 payload, and as the once-per-call guard for the
     *  lead_capture_v1 client-side tool. */
    sessionId: string;
    leadCaptureFired: boolean;
  }>({
    context: null,
    mode: 'customer',
    allMessages: [],
    actionsTaken: [],
    startTime: 0,
    sessionId: '',
    leadCaptureFired: false,
  });
  /* ── Log helper — adds to visible log (last 3) + session transcript ──
     DEDUPED (Eri round 2 item 5): Vapi re-emits final transcript segments,
     so an identical consecutive line from the same speaker is dropped here,
     BEFORE it can reach the visible log, the folded chat history, or the
     session transcript - the "repeats itself" loop dies at the source. */
  const lastLogRef = useRef<{ role: string; text: string }>({ role: '', text: '' });
  const addLog = useCallback((text: string, role: LogMessage['role']) => {
    const norm = (text || '').trim();
    if ((role === 'user' || role === 'assistant') &&
        lastLogRef.current.role === role && lastLogRef.current.text === norm) {
      return; // duplicate final segment, already logged
    }
    if (role === 'user' || role === 'assistant') {
      lastLogRef.current = { role, text: norm };
      sessionRef.current.allMessages.push({ role, text });
    }
    // Don't add system/tool messages to visible log
    if (role === 'system' || role === 'tool') return;
    setLogs(prev => {
      const newLogs = [...prev, {
        id: Math.random().toString(36),
        timestamp: new Date(),
        role,
        text,
      }];
      return newLogs.slice(-3);
    });
  }, []);
  /* ── Stable refs for callbacks to avoid re-running the effect ── */
  const onShowCardRef = useRef(onShowCard);
  useEffect(() => {
    onShowCardRef.current = onShowCard;
  }, [onShowCard]);
  const onShowBlocksRef = useRef(onShowBlocks);
  useEffect(() => {
    onShowBlocksRef.current = onShowBlocks;
  }, [onShowBlocks]);
  const onLookupSubscriberRef = useRef(onLookupSubscriber);
  useEffect(() => {
    onLookupSubscriberRef.current = onLookupSubscriber;
  }, [onLookupSubscriber]);
  const onJarvisCommandRef = useRef(onJarvisCommand);
  useEffect(() => {
    onJarvisCommandRef.current = onJarvisCommand;
  }, [onJarvisCommand]);
  const onResolvePreviewRef = useRef(onResolvePreview);
  useEffect(() => {
    onResolvePreviewRef.current = onResolvePreview;
  }, [onResolvePreview]);
  const onResolveFireRef = useRef(onResolveFire);
  useEffect(() => {
    onResolveFireRef.current = onResolveFire;
  }, [onResolveFire]);
  /* ══════════════════════════════════════════════════════
     VAPI EVENT LISTENERS
     Set up once on mount, cleaned up on unmount.
     ══════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!vapiRef.current && (VAPI_PUBLIC_KEY || VOICE_SIM)) {
      // Sim transport (no-phone battery only): same listeners, same handlers,
      // fake wire. Prod pages never set the flag, so this branch is dead there.
      vapiRef.current = VOICE_SIM
        ? (((window as any).__ORBI_VAPI_SIM__ = new SimVapiTransport()) as unknown as Vapi)
        : new Vapi(VAPI_PUBLIC_KEY);
      const vapi = vapiRef.current;
      /* Call lifecycle */
      vapi.on('call-start', () => {
        clearConnectTimeout();
        setMicError(null);
        setConnectionState(ConnectionState.CONNECTED);
        setAgentState('listening');
      });
      vapi.on('call-end', () => {
        clearConnectTimeout();
        recentToolCalls.current.clear();
        clearFarewellTimer();
        setConnectionState(ConnectionState.DISCONNECTED);
        setAgentState('idle');
        setAudioLevel(0);
        setLogs([]);
        // Report session end for Haiku processing
        const s = sessionRef.current;
        if (s.context && s.allMessages.length > 0) {
          reportSessionEnd(s.context, s.mode, s.allMessages, s.actionsTaken, s.startTime);
        }
      });
      /* Speech detection */
      vapi.on('speech-start', () => {
        clearFarewellTimer(); // User is talking — cancel farewell auto-end
        setAgentState('listening');
      });
      vapi.on('speech-end', () => {
        setAgentState('processing');
      });
      /* Messages: transcripts + tool calls */
      vapi.on('message', (message: any) => {
        if (message.type === 'transcript') {
          if (message.transcriptType === 'final') {
            if (message.role === 'user') {
              clearFarewellTimer(); // User responded — cancel agent farewell
              addLog(message.transcript, 'user');
              setAgentState('processing');
              lastUserTranscript.current = message.transcript.toLowerCase();
              // Also detect user farewell — auto-end if agent doesn't respond
              const userText = message.transcript.toLowerCase();
              const isUserFarewell = FAREWELL_PATTERNS.some(phrase => userText.includes(phrase));
              if (isUserFarewell) {
                farewellTimer.current = setTimeout(() => {
                  console.log('[Orbi] User farewell detected, no agent response — auto-ending call');
                  vapiRef.current?.stop();
                }, FAREWELL_SILENCE_MS);
              }
            } else if (message.role === 'assistant') {
              addLog(message.transcript, 'assistant');
              setAgentState('speaking');
              // Check if Orbi is saying goodbye → start auto-end timer
              const text = message.transcript.toLowerCase();
              const isFarewell = FAREWELL_PATTERNS.some(phrase => text.includes(phrase));
              if (isFarewell) {
                clearFarewellTimer();
                farewellTimer.current = setTimeout(() => {
                  console.log('[Orbi] Farewell complete — ending call');
                  vapiRef.current?.stop();
                }, 3500); // 3.5s after farewell speech ends = call drops cleanly
              }
            }
          }
        } else if (message.type === 'function-call') {
          // Legacy VAPI event format — keep for backwards compatibility
          const skillName = message.functionCall?.name || 'unknown';
          addLog(`Running ${skillName}...`, 'tool');
          sessionRef.current.actionsTaken.push({
            skill: skillName,
            success: true,
          });
          setToolCallCount(prev => prev + 1);
        } else if (message.type === 'tool-calls') {
          // Current VAPI tool call format
          const toolCallList = message.toolCallList || [];
          for (const toolCall of toolCallList) {
            const skillName = toolCall.function?.name || 'unknown';
            const now = Date.now();
            const lastCall = recentToolCalls.current.get(skillName);
            // jarvis_navigate is exempt from the duplicate guard: back-to-back
            // navigations ("customer 55102"... "now billing") are legitimate.
            // show_resolve_preview likewise (zero-write; a re-preview right
            // after a navigation or a fresh outage read is legitimate).
            // fire_mass_resolve is NOT exempt: the guard is a second layer on
            // top of the confirm + the server's idempotent dedup.
            if (lastCall && now - lastCall < 30000 && skillName !== 'jarvis_navigate' && skillName !== 'show_resolve_preview') {
              console.warn(`[Orbi] Duplicate tool call blocked: ${skillName} (${now - lastCall}ms since last)`);
              vapiRef.current?.send({
                type: 'add-message',
                message: {
                  role: 'tool' as const,
                  toolCallId: toolCall.id,
                  content: 'Done. Escalation is already being processed — a technician will call the customer back shortly.',
                },
              });
              continue;
            }
            recentToolCalls.current.set(skillName, now);
            // Safety net: if user asked for a human but model chose wrong tool, force escalation
            if ((skillName === 'endCall' || skillName === 'show_card') &&
                ESCALATION_INTENT_PHRASES.some(p => lastUserTranscript.current.includes(p))) {
              console.log('[Orbi] Safety net: forced escalation — model chose wrong tool');
              const escalateSkill = sessionRef.current.context?.skills?.find(s => s.name === 'escalate_to_agent');
              if (escalateSkill?.webhook_url) {
                fetch(escalateSkill.webhook_url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    account_number: sessionRef.current.context?.account_number,
                    first_name: sessionRef.current.context?.first_name,
                    reason: 'Customer requested human — model chose wrong tool, safety net fired',
                  }),
                }).catch(err => console.warn('[Orbi] Safety net escalation failed:', err));
                addLog('Escalating to support team (safety net)...', 'tool');
                sessionRef.current.actionsTaken.push({ skill: 'escalate_to_agent', success: true });
              }
              vapiRef.current?.send({
                type: 'add-message',
                message: {
                  role: 'tool' as const,
                  toolCallId: toolCall.id,
                  content: 'Escalation sent. A technician will call the customer back shortly.',
                },
              });
              lastUserTranscript.current = '';
              continue;
            }
            if (skillName === 'endCall') {
              console.log('[Orbi] AI triggered endCall — waiting for farewell to complete');
              clearFarewellTimer();
              // Don't start the timer yet — wait for the farewell speech to actually start playing
              // The farewell safety timer in the transcript handler will end the call
              // We just set a hard backstop here of 12s from tool fire
              farewellTimer.current = setTimeout(() => {
                console.log('[Orbi] endCall backstop — disconnecting');
                vapiRef.current?.stop();
              }, 12000);
              return;
            } else if (skillName === 'lead_capture_v1') {
              // Client-side tool. Compose the canonical inbound payload
              // expected by the ORA Subscriber Interaction Classifier
              // (Make.com scenario 5116824) and POST to the per-altitude
              // webhook resolved at session start. Patent #9 evidence is
              // written by the classifier into subscriber_interaction_signals.
              const args = toolCall.function?.arguments || {};
              const routing = sessionRef.current.context?.lead_routing;
              const session = sessionRef.current;
              const respond = (content: string) => {
                vapiRef.current?.send({
                  type: 'add-message',
                  message: {
                    role: 'tool' as const,
                    toolCallId: toolCall.id,
                    content,
                  },
                });
              };
              if (!routing) {
                console.warn('[Orbi] lead_capture_v1 fired but no lead_routing on context');
                respond('Lead capture is not configured for this session. Apologise to the caller and offer to take a callback through the support team.');
                continue;
              }
              if (session.leadCaptureFired) {
                console.warn('[Orbi] lead_capture_v1 fired twice, second call blocked');
                respond('Already captured. Confirm to the caller that their details are saved and continue the conversation.');
                continue;
              }
              session.leadCaptureFired = true;
              const customerName = String(args.customer_name ?? '').trim();
              const phone = String(args.phone ?? '').trim();
              const email = String(args.email ?? '').trim();
              const need = String(args.need ?? '').trim();
              const payload = {
                source: 'voice_orbi',
                vertical_id: routing.vertical_id,
                client_id: routing.client_id,
                subscriber_id: null,
                session_id: session.sessionId,
                message_text:
                  `Lead intake: name=${customerName} phone=${phone} email=${email} need=${need}`,
                message_metadata: {
                  channel: 'voice_orbi',
                  altitude_level: routing.altitude_level,
                  captured_fields: {
                    customer_name: customerName,
                    phone,
                    email,
                    need,
                  },
                },
              };
              addLog('Saving lead details...', 'tool');
              session.actionsTaken.push({ skill: 'lead_capture_v1', success: true });
              setToolCallCount(prev => prev + 1);
              (async () => {
                try {
                  const t0 = performance.now();
                  const res = await fetch(routing.webhook_url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                  });
                  const elapsed = Math.round(performance.now() - t0);
                  console.log(`[Orbi timing] lead_capture_v1_ms=`, elapsed, 'status=', res.status);
                  if (!res.ok) {
                    const txt = await res.text().catch(() => '');
                    console.warn('[Orbi] lead_capture_v1 webhook non-2xx:', res.status, txt.slice(0, 200));
                    // DISCLOSURE GUARDRAIL: never name the transport ("webhook") to the model —
                    // it repeats such words to the caller. Generic + honest only.
                    respond('The lead could not be saved just now. Apologise to the caller without giving any technical reason, take down a callback number verbally, and tell them the team will reach out within one business day.');
                    return;
                  }
                  console.log('[Orbi] lead_capture_v1 fired ok, payload sent for classification');
                  const firstName = customerName.split(/\s+/)[0] || 'there';
                  respond(
                    `Lead captured. Confirm to the caller by name (${firstName}) that you have their details and the team will be in touch within one business day. Do not show a card.`
                  );
                } catch (err) {
                  console.error('[Orbi] lead_capture_v1 webhook error:', err);
                  respond('The lead could not be saved just now. Apologise warmly without giving any technical reason, confirm verbally that the team will follow up, and continue.');
                }
              })();
            } else if (skillName === 'show_blocks') {
              // Phase 4 voice parity: registry blocks spun up while speaking.
              // Raw JSON only; the callback validates through validateUiBlocks
              // (the same registry as the typed ask path) before anything paints.
              let rawBlocks: unknown = (toolCall.function?.arguments || {}).blocks;
              if (typeof rawBlocks === 'string') {
                try { rawBlocks = JSON.parse(rawBlocks); } catch { rawBlocks = []; }
              }
              const shown = onShowBlocksRef.current ? onShowBlocksRef.current(rawBlocks) : { types: [] };
              setToolCallCount(prev => prev + 1);
              vapiRef.current?.send({
                type: 'add-message',
                message: {
                  role: 'tool' as const,
                  toolCallId: toolCall.id,
                  content: shown.types.length
                    ? `Displayed ${shown.types.length} block${shown.types.length > 1 ? 's' : ''} (${shown.types.join(', ')}). The operator can see and tap them; any call button waits for their go.`
                    : 'No block passed validation, so nothing was displayed. Continue in speech only; do not retry with invented values.',
                },
              });
            } else if (skillName === 'lookup_subscriber') {
              // Subject-by-voice (round 2 item 6): resolve the spoken name via
              // the lens-scoped search, prime the subject, hand the agent the
              // full live record. Async: the tool result follows when the
              // reads land; the honest failure line keeps the agent grounded.
              const args = toolCall.function?.arguments || {};
              const spokenName = typeof args === 'string' ? args : (args.name || '');
              setToolCallCount(prev => prev + 1);
              void (async () => {
                let content = 'Lookup unavailable right now; ask the operator to open the subscriber instead.';
                try {
                  if (onLookupSubscriberRef.current && spokenName) {
                    const r = await onLookupSubscriberRef.current(String(spokenName));
                    content = JSON.stringify(r);
                  }
                } catch (err) {
                  // DISCLOSURE GUARDRAIL: operator altitude may hear WHICH capability failed,
                  // never the raw error (it can carry URLs, table names, or status codes).
                  console.warn('[Orbi] resolve_subscriber lookup failed:', err);
                  content = 'The subscriber lookup did not respond. Say so plainly, with no technical detail; do not invent a subscriber.';
                }
                vapiRef.current?.send({
                  type: 'add-message',
                  message: { role: 'tool' as const, toolCallId: toolCall.id, content },
                });
              })();
            } else if (skillName === 'jarvis_navigate') {
              // ONE-BRAIN (JARVIS NAV): the spoken command goes through the
              // deterministic classifier the typed path uses. The structured
              // result comes back as the tool content so the agent narrates
              // exactly what happened - navigated in place, answered on the
              // panel, or no match (answer in speech).
              const args = toolCall.function?.arguments || {};
              const utterance = typeof args === 'string' ? args : (args.utterance || '');
              setToolCallCount(prev => prev + 1);
              addLog('Navigating...', 'tool');
              void (async () => {
                // SPEECH SYNC: every path out of here is a structured result
                // with an explicit navigated flag, so the agent's words always
                // derive from what ACTUALLY happened - even the failure paths.
                let content = JSON.stringify({
                  matched: false, navigated: false,
                  reason: 'navigation is not wired up in this session',
                  note: 'The screen did NOT open; say so honestly if they asked to go somewhere, and answer in speech.',
                });
                try {
                  if (onJarvisCommandRef.current && utterance) {
                    const r = await onJarvisCommandRef.current(String(utterance));
                    content = JSON.stringify(r);
                  }
                } catch (err) {
                  // KEEP BOTH: DISCLOSURE GUARDRAIL + nav SPEECH GATE.
                  // Guardrail: log the real error to the console only; never
                  // interpolate err.message into model-facing content.
                  console.warn('[Orbi] jarvis_navigate failed:', err);
                  // Speech gate: still return the STRUCTURED result with an
                  // explicit navigated:false flag so the agent narrates that
                  // the screen did NOT open (and answers in speech) - the
                  // spoken outcome derives from the actual nav result, with
                  // no technical detail leaked.
                  content = JSON.stringify({
                    matched: false, navigated: false,
                    reason: 'navigation failed',
                    note: 'The screen did NOT open; say so honestly if they asked to go somewhere, and answer in speech.',
                  });
                }
                sessionRef.current.actionsTaken.push({ skill: 'jarvis_navigate', success: content.includes('"navigated":true') });
                vapiRef.current?.send({
                  type: 'add-message',
                  message: { role: 'tool' as const, toolCallId: toolCall.id, content },
                });
              })();
            } else if (skillName === 'show_resolve_preview' || skillName === 'fire_mass_resolve') {
              // JARVIS SITUATIONAL AWARENESS: the voice face of the governed
              // outage-mass-resolve endpoint. Preview is zero-write; fire is
              // relayed ONLY after the operator's explicit on-screen confirm
              // (the dock owns that gate). The structured result comes back
              // as the tool content so the agent narrates what really
              // happened - never an assumed success.
              const isFire = skillName === 'fire_mass_resolve';
              setToolCallCount(prev => prev + 1);
              addLog(isFire ? 'Mass resolve: awaiting operator confirm...' : 'Building mass-resolve preview...', 'tool');
              sessionRef.current.actionsTaken.push({ skill: skillName, success: true });
              void (async () => {
                let content = 'Mass resolve is not available right now; say so plainly.';
                try {
                  const fn = isFire ? onResolveFireRef.current : onResolvePreviewRef.current;
                  if (fn) content = JSON.stringify(await fn());
                } catch (err) {
                  // DISCLOSURE GUARDRAIL: no raw error text to the model.
                  console.warn('[Orbi] mass resolve failed:', err);
                  content = 'The mass resolve step did not respond. Say so plainly, with no technical detail; do not retry on your own.';
                }
                vapiRef.current?.send({
                  type: 'add-message',
                  message: { role: 'tool' as const, toolCallId: toolCall.id, content },
                });
              })();
            } else if (skillName === 'show_card') {
              // Client-side tool — trigger card display in VoiceOverlay
              const args = toolCall.function?.arguments || {};
              const rawCardType = args.card_type;
              // Phase 0.5: normalise mis-fired card_types (e.g. billing_alert → account)
              const cardType = rawCardType && CARD_TYPE_ALIASES[rawCardType]
                ? CARD_TYPE_ALIASES[rawCardType]
                : rawCardType;
              if (cardType && onShowCardRef.current) {
                if (rawCardType !== cardType) {
                  console.log(`[Orbi] show_card alias: "${rawCardType}" → "${cardType}"`);
                }
                console.log(`[Orbi] AI triggered show_card: ${cardType}`, args);
                onShowCardRef.current(cardType, args);
                // Send tool result back so Orbi knows what's on screen
                const heading = args.heading || cardType;
                const metric = args.metric ? ` Metric: "${args.metric}".` : '';
                vapiRef.current?.send({
                  type: 'add-message',
                  message: {
                    role: 'tool' as const,
                    toolCallId: toolCall.id,
                    content: `Card displayed: ${cardType}. Heading: "${heading}".${metric} Customer can see this on their screen.`,
                  },
                });
              }
            } else {
              // Server-side skill — log it
              addLog(`Running ${skillName}...`, 'tool');
              sessionRef.current.actionsTaken.push({
                skill: skillName,
                success: true,
              });
              setToolCallCount(prev => prev + 1);
            }
          }
        }
      });
      /* Audio level for orb reactivity */
      vapi.on('volume-level', (level: number) => {
        setAudioLevel(level);
        if (level > 0.01) {
          setAgentState('speaking');
        }
      });
      /* Error handling. The Vapi/Daily join failure this most often maps to is
       * a denied or unavailable microphone — call that out by name so "fail
       * open" means something the operator can act on, not just a dead end. */
      vapi.on('error', (error: any) => {
        console.error('VAPI error:', error);
        clearFarewellTimer();
        clearConnectTimeout();
        setMicError(describeMicError(error));
        setConnectionState(ConnectionState.ERROR);
      });
    }
    return () => {
      clearFarewellTimer();
      if (vapiRef.current) {
        vapiRef.current.stop();
      }
    };
  }, [addLog, clearFarewellTimer]);
  /* ══════════════════════════════════════════════════════
     START CALL
     Builds system prompt + tools from context, starts VAPI.
     ══════════════════════════════════════════════════════ */
  const startCall = useCallback(async (context: CustomerContext, mode: OrbiMode = 'customer') => {
    if (!vapiRef.current) {
      // Nothing was ever wired up (VAPI_PUBLIC_KEY missing/unset at mount).
      // Previously this returned silently — no state change at all — so the
      // click had literally no visible effect. Say so instead of nothing.
      console.error('[Orbi] startCall: Vapi client was never initialised (missing VAPI_PUBLIC_KEY?)');
      setMicError("Couldn't start the voice call. Please try again or reload the page.");
      setConnectionState(ConnectionState.ERROR);
      return;
    }
    // Reset session tracking
    const sessionId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionRef.current = {
      context,
      mode,
      allMessages: [],
      actionsTaken: [],
      startTime: Date.now(),
      sessionId,
      leadCaptureFired: false,
    };
    setToolCallCount(0);
    clearFarewellTimer();
    setMicError(null);
    setConnectionState(ConnectionState.CONNECTING);
    // FAIL OPEN: guarantee the mic control recovers with a visible reason even
    // if the SDK never emits 'call-start' or 'error' (e.g. the browser's mic
    // permission prompt sits unanswered). Cleared the moment we get a real
    // outcome (call-start, error, or stop); see those handlers above.
    clearConnectTimeout();
    connectTimeoutRef.current = setTimeout(() => {
      connectTimeoutRef.current = null;
      console.warn('[Orbi] startCall: no response after 20s, forcing fail-open');
      setMicError('Still waiting on the microphone. Check your browser\'s microphone permission and try again.');
      setConnectionState(ConnectionState.ERROR);
    }, 20000);
    try {
      // Build tools: dynamic skills from context + show_card client-side tool
      const tools = buildToolsFromContext(context);
      console.log(`[Orbi ${mode}] Loaded ${tools.length} tools:`, tools.map(t => t.function.name));
      // Log memory state
      if (context.memory) {
        const m = context.memory;
        console.log(
          `[Orbi] Memory loaded: ${m.recent_sessions?.length || 0} sessions, ` +
          `insights: ${m.customer_insights ? 'yes' : 'no'}, ` +
          `system: ${m.system_insights?.length || 0}`
        );
      }
      // Build first message + system prompt. OPERATOR mode is fully isolated from
      // the customer/agent assembly below: Orbi is the staff copilot, never a
      // customer, never the NOC field tech. It is primed with the on-screen scope
      // (no subscriber PII) and stays read-only.
      let firstMessage: string;
      let systemPrompt: string;
      if (mode === 'operator') {
        const op = (context as any)._operator || {};
        const scopeLabel: string = op.scope_label || 'the operations console';
        const onScreen = op.section ? `the ${op.section} view for ${scopeLabel}` : `${scopeLabel}`;
        const subj = op.subject && (op.subject.name || op.subject.account_number) ? op.subject : null;
        const subjLabel = subj ? (subj.name || `account ${subj.account_number}`) : '';
        // SHORT INTRO (round 4 fix): no name, no "recorded line" disclosure,
        // no monologue - a short warm opener, randomly picked each session.
        // This is an internal operator tool, not a customer call; the screen,
        // subject, and lens still reach the model via the system prompt below,
        // just not the spoken opener. The resolve pill's two situational
        // lines stay - the operator explicitly pressed Resolve, so opening on
        // that beats a generic greeting - but they drop the name too.
        const OPERATOR_GREETINGS = [
          'Hey, good to see you. How can I help?',
          'Hi there. What can I help you with?',
          'Hey. Where would you like to start?',
          'Hi, happy to help. What are we looking at today?',
          'Hey, I’m here whenever you’re ready. What can I do?',
        ];
        const greetLine = OPERATOR_GREETINGS[Math.floor(Math.random() * OPERATOR_GREETINGS.length)];
        firstMessage = (typeof op.resolve_situation === 'string' && op.resolve_situation)
          ? (op.mass_resolve === true
              ? `I have the outage up - say go and I'll show who we'd call.`
              : `I see the down connections; no active outage yet. How can I help?`)
          : greetLine;
        const lensLine = op.altitude === 'client' && op.client_name
          ? `The console is looking through the ${op.client_name} client lens; figures are scoped to that client. `
          : `The console is at vertical altitude, across all clients${Array.isArray(op.clients) && op.clients.length ? ` (${op.clients.join(', ')})` : ''}. `;
        // Full subject record (round 2 item 7): when the dock fetched the live
        // detail, spell it out so the agent discusses the REAL account, not a
        // thin name+number subset. Values are live rows; absent stays absent.
        const d = op.subject_detail || null;
        const detailLine = d ? (() => {
          const bits: string[] = [];
          if (d.status) bits.push(`status ${d.status}`);
          if (d.billing_status) bits.push(`billing ${d.billing_status}`);
          if (d.plan_name || d.plan_speed_mbps) bits.push(`plan ${d.plan_name || `${d.plan_speed_mbps} Mbps`}`);
          if (d.connection_status) bits.push(`connection ${d.connection_status}`);
          if (typeof d.trust_confidence === 'number') bits.push(`trust confidence ${d.trust_confidence}`);
          else bits.push('no trust score yet (no conversation history has formed one; it builds as calls and chats land)');
          if (typeof d.open_ticket_count === 'number') bits.push(`${d.open_ticket_count} open ticket${d.open_ticket_count === 1 ? '' : 's'}`);
          if (d.recurring_issues) bits.push(`recurring issues: ${String(d.recurring_issues).slice(0, 140)}`);
          if (d.risk_flags) bits.push(`risk flags: ${String(d.risk_flags).slice(0, 100)}`);
          return bits.length ? `Their live record: ${bits.join('; ')}. ` : '';
        })() : '';
        const subjLine = subj
          ? `The operator has subscriber ${subjLabel}${subj.account_number && subj.name ? ` (account ${subj.account_number})` : ''} open in a panel; "this customer" means them. ${detailLine}`
          : `NO subscriber is open right now. If the operator asks about "this customer" without naming one, say plainly that no subscriber is open and ask which one they mean; NEVER guess or pick an account yourself. `;
        const lookupLine =
          `When the operator NAMES a subscriber or account number you do not already have loaded, call lookup_subscriber with what they said: it primes that subscriber and returns their live record (status, billing, plan, connection, trust confidence, open tickets). Discuss them from that returned data ONLY; if it returns candidates, read the names back and ask which; if not found, say so. `;
        systemPrompt =
          `You are the operator copilot inside ${scopeLabel}, the ISP's own staff console. The person talking to you is a STAFF MEMBER already inside their console: ` +
          `NEVER ask them to identify themselves, share an account number for themselves, or verify anything, and never treat them as a caller. ` +
          `Do not adopt a customer-support greeting or a NOC field-tech script. ` +
          `Do not repeat your greeting, the view description, or context you have already said this call; say each thing once. ` +
          lensLine + subjLine + lookupLine +
          (op.section ? `Current view: ${op.section} for ${scopeLabel}. ` : '') +
          // SECTION DESK (Phase 3 binding 1): who answers for this view - or,
          // when the binding catalogue loaded and holds no owner, the honest
          // fail-closed line. Absent both, nothing is claimed either way.
          (op.section_desk
            ? `This view is owned by the ${op.section_desk} desk; for questions about this section, answer as that desk and keep to this section's own data. `
            : op.section_desk_missing === true
              ? `No desk persona is configured for this view yet. If the operator asks who owns this section, say so plainly - never invent or guess an owner. `
              : '') +
          (op.recent_sections ? `They recently moved through: ${op.recent_sections}. ` : '') +
          // ONE PATH (audit A1): the typed panel and this call are ONE
          // conversation. What the operator typed (and was answered) before
          // pressing Talk is context you already hold - never claim ignorance
          // of it, and never re-ask for something it already establishes.
          (op.recent_thread
            ? `RECENT CONVERSATION (the same assistant panel, typed, just now - continue it, do not restart it):\n${op.recent_thread}\n`
            : '') +
          // JARVIS SITUATIONAL AWARENESS: the live on-screen situation, read
          // from the page's published envelope at call start. When the
          // operator opens with "what's happening, a lot is down", this is
          // already in hand - never say you need to look at the page.
          (op.situation ? `LIVE SITUATION (read from the screen just now; updates arrive as context messages when the screen changes): ${op.situation} ` : '') +
          // SECTION WATCH (Phase 3 binding 2): the section's work-stream lead,
          // already read before the call - open with it when the operator asks
          // what needs attention; the figures are the feed's, never invented.
          (op.section_watch ? `SECTION WATCH (read from this section's work-stream feed just now): ${op.section_watch} ` : '') +
          (op.capability_lines
            ? `CAPABILITIES you carry in this console (config-resolved for this tenant). When the operator asks for help with something one of these covers, NAME it and offer to run it - never draw a blank or say you have no way to help while a listed capability covers the ask:\n${op.capability_lines}\n`
            : '') +
          `You help the operator understand what is on their screen and across the platform: subscribers, ` +
          `network health, billing, tickets, and what the system remembers. You are READ ONLY - you surface ` +
          `and explain, you never change records. Keep replies short and spoken-natural. ` +
          // MAP HONESTY (round 3 item 6): no tool exists to pan/zoom the network
          // map - a prior session claimed it zoomed to the affected area while
          // the map never moved. Never claim a map action with no tool behind
          // it; jarvis_navigate opening the screen is not the same as moving
          // the map on it.
          `You cannot pan, zoom, or otherwise control the map on the network view - there is no tool for that. If asked to zoom to an area, say plainly that you cannot control the map yet and offer to navigate to the screen or describe what is on it instead. Never claim to have moved, zoomed, or focused the map.` +
          (op.jarvis_nav === true
            ? ` NAVIGATION LAW: when the operator asks to open, show, or go anywhere - a screen, a subscriber, an account number, a building - call jarvis_navigate with their exact words BEFORE anything else. It navigates IN PLACE while this call continues; the call never drops. Never say you cannot navigate mid-call and never defer a navigation to after the call. If it returns matched:false, answer yourself from your context and tools. SPEECH GATE (STRICT): say NOTHING about the navigation until the jarvis_navigate tool result is in your hands - the tool speaks its own brief "one sec" filler for you, so stay silent until the result returns; do not improvise a status while you wait. When navigated:true, give ONE short warm confirmation ("Here you go", "That's up now", "Got it, take a look") - nothing more. When navigated:false, say ONLY the honest reason the result carries. FORBIDDEN, always: you must NEVER tell the operator you hit a snag, ran into trouble, have a connectivity or connection issue, hit an error or a glitch, that the link or navigation is not working, or anything of that shape - navigation succeeds silently and near-instantly, so a failure word after a successful move is a lie the operator hears. Never state an outcome the result does not state.`
            : '') +
          (typeof op.resolve_situation === 'string' && op.resolve_situation
            ? ` SITUATION: ${op.resolve_situation}`
            : '') +
          (op.mass_resolve === true
            ? ` OUTAGE RESOLVE LAW: this session exists to resolve that outage. When the operator wants to resolve, call, or sweep the affected customers, call show_resolve_preview FIRST - it opens the who-gets-called list on the RESOLVE BOARD (the screen navigates there in place, the call stays live) showing exactly who WILL be called and every excluded group with its reason. SPEECH GATE (STRICT): say NOTHING about the preview until the show_resolve_preview result is in your hands - the tool speaks its own brief "let me pull that up" filler for you, so stay silent until the result returns; do not improvise a status while you wait. When the result comes back, the list IS on the operator's screen - confirm that briefly and narrate the gist (how many called, why the rest are excluded, TEST MODE or live), then WAIT. FORBIDDEN, always: you must NEVER apologise for not showing the list, say the list did not load or did not show, say you hit a snag or an error or a connectivity issue, or claim the preview failed - navigation to the board succeeds and the list renders there, so any "it didn't show" line is a lie the operator hears while looking right at it. If the result did not carry exact counts, say the full list is on the Resolve board for them to review - never that it failed. Call fire_mass_resolve ONLY after the operator explicitly confirms out loud ("fire", "go ahead", "call them"). The operator can also confirm by clicking the board's own Confirm & fire button - if they say they clicked it, do not fire again. Never fire without the list on screen, and never state a count the preview did not return. While TEST MODE is on, a sweep can only dial the staff allowlist, never a customer - say so when the preview reports test_mode.`
            : '') +
          (op.generative_ui === true
            ? ` ALWAYS SHOW while you speak: whenever you state a concrete figure, discuss a subscriber, or list anything, call show_blocks with the matching registry block - subscriber_card for one subscriber (do this right after a successful lookup_subscriber), metric_chips for counts, table for a short list, trust_donut ONLY when you hold a real confidence value. show_blocks is your display tool; do NOT use show_card when show_blocks is available. Use values you actually know; never invent one. At most 2 blocks per call and only when they add something over speech. A call action is a real dial button the operator taps; offer it as "On your go, I can call {name}". A navigate action opens that screen ${op.jarvis_nav === true ? 'in place while this call continues' : 'after this call ends'}.`
            : ` When you state a concrete figure or a specific record, you may call show_card to put it on screen.`) +
          // TTS FIX (2026-08-26): the composer's rule-20 discipline, missing
          // from the operator branch since it never calls the composer - counts
          // in words, references digit by digit, place names as written.
          OPERATOR_SPEECH_RULES;
      } else {
        // Build first message — unknown caller first, then persona greeting, then template
        firstMessage = isUnknownCaller(context)
          ? "Hi there, this is the AI assistant from your internet service provider. I don't seem to have your details on file — could you share your name and account number so I can help you?"
          : (context.persona?.persona_key === 'customer_acquisition' && (context.persona as any)?.greeting)
          ? String((context.persona as any).greeting).replace(/\{first_name\}/gi, '')
          : context.persona_greeting
          ? context.persona_greeting.replace(/\{first_name\}/gi, context.first_name)
          : mode === 'agent'
          ? `I've pulled up account ${context.account_number} for ${context.first_name} ${context.last_name}. ${
              context.connection_status === 'degraded'
                ? `Connection is degraded — ${context.current_speed_mbps} of ${context.plan_speed_mbps} Mbps. I have diagnostic details ready. What would you like to check?`
                : context.connection_status === 'down'
                ? `Connection is currently down. I have the diagnostic data. How would you like to proceed?`
                : `Connection looks healthy at ${context.current_speed_mbps} Mbps. What's the issue?`
            }`
          : `Hi ${context.first_name}, this is the AI assistant from your internet service provider. ${
              context.connection_status === 'degraded'
                ? `I can see your connection is running a bit slow right now at ${context.current_speed_mbps} megabits instead of your usual ${context.plan_speed_mbps}. I'm here to help with that.`
                : context.connection_status === 'down'
                ? `I can see your connection seems to be having some trouble right now. Let me help you get back online.`
                : `How can I help you today?`
            }`;
        // Data-driven personas (e.g. Zara / customer_acquisition) need an
        // async fetch of their Supabase payload before assembly; every
        // other persona falls through to the existing sync buildSystemPrompt
        // unchanged.
        const personaKey = context.persona?.persona_key
          ?? (mode === 'customer' ? 'customer_support' : 'network_engineer');
        systemPrompt = await buildSystemPromptAsync(context, mode, personaKey);
      }
      console.log('[Orbi] System prompt length:', systemPrompt?.length,
        'Rule 9 present:', systemPrompt?.includes('MANDATORY PRE-TOOL NARRATION'),
        'Rule 10 present:', systemPrompt?.includes('MANDATORY FAREWELL BEFORE endCall'));
      // TTS/ASR FIX (2026-08-26): the on-screen proper nouns the dock handed
      // over (_operator.keyterms - client names, outage areas, the open
      // subscriber), normalised and budget-capped in ONE place (speech.js).
      const operatorKeyterms = mode === 'operator'
        ? buildDockKeyterms((context as any)._operator?.keyterms)
        : [];
      await vapiRef.current.start({
        model: {
          provider: 'anthropic',
          model: 'claude-sonnet-4-20250514',
          messages: [
            {
              role: 'system',
              content: systemPrompt,
            },
          ],
          tools,
          temperature: 0.45,
          maxTokens: mode === 'customer' ? 300 : 500,
        },
        voice: resolveVapiVoice((context as any).persona_voice),
        firstMessage,
        name: mode === 'operator' ? 'Orbi Operator Copilot' : mode === 'agent' ? 'Orbi Support Copilot' : 'Orbi ISP Support',
        // TTS/ASR FIX (2026-08-26), operator mode only: the phone rail moved to
        // nova-3 + smartFormat + a keyterm list long ago (orbi-inbound-assistant;
        // cities stay in the keyterm list so the transcriber still hears them)
        // and the dock never did - which is how "Wichita Falls" transcribed as
        // "Witcher Falls". The dock primes the terms from what is on screen
        // (_operator.keyterms via buildDockKeyterms); keyterm is nova-3-only,
        // so the model moves with it. Customer/agent modes stay byte-identical.
        transcriber: mode === 'operator'
          ? {
              provider: 'deepgram',
              model: 'nova-3',
              language: 'multi',
              smartFormat: true,
              ...(operatorKeyterms.length ? { keyterm: operatorKeyterms } : {}),
            }
          : {
              provider: 'deepgram',
              model: 'nova-2',
              language: 'multi',
            },
        // BARGE-IN (round 4 fix, verified against @vapi-ai/web's own type defs):
        // the previous numWords:1 was the bug - per Vapi's own docs, voiceSeconds
        // (the fast VAD-based interrupt) is "only used if numWords is set to 0";
        // numWords:1 instead makes interruption wait for a full TRANSCRIBED word,
        // which has real STT latency and is not "the instant I speak". numWords:0
        // activates the VAD path so a voice spike alone interrupts.
        // firstMessageInterruptionsEnabled defaults to FALSE in the SDK - the
        // greeting itself could not be interrupted at all regardless of
        // stopSpeakingPlan, which is exactly the reported "still not working".
        firstMessageInterruptionsEnabled: true,
        stopSpeakingPlan: {
          numWords: 0,
          voiceSeconds: 0.1,
          backoffSeconds: 0.8,
        },
      });
    } catch (error) {
      console.error('Failed to start VAPI call:', error);
      clearConnectTimeout();
      setMicError(describeMicError({ error }));
      setConnectionState(ConnectionState.ERROR);
    }
  }, [clearFarewellTimer, clearConnectTimeout]);
  /* ══════════════════════════════════════════════════════
     STOP CALL
     ══════════════════════════════════════════════════════ */
  const stopCall = useCallback(async () => {
    clearFarewellTimer();
    clearConnectTimeout();
    if (vapiRef.current) {
      vapiRef.current.stop();
    }
    setConnectionState(ConnectionState.DISCONNECTED);
    setAgentState('idle');
    setAudioLevel(0);
    setLogs([]);
  }, [clearFarewellTimer, clearConnectTimeout]);
  /* ── Send a message as the user (appears in transcript, AI responds naturally) ── */
  const sendMessage = useCallback((text: string) => {
    if (vapiRef.current) {
      vapiRef.current.send({
        type: 'add-message',
        message: { role: 'user', content: text },
      });
    }
  }, []);
  /* ── Send SILENT context (JARVIS SITUATIONAL AWARENESS): a system message
     the agent holds without speaking a reply, so a navigation can re-brief
     the situation BEFORE the operator's next word rather than triggering an
     unprompted monologue. ── */
  const sendContext = useCallback((text: string) => {
    if (vapiRef.current) {
      vapiRef.current.send({
        type: 'add-message',
        message: { role: 'system', content: text },
      });
    }
  }, []);
  /* ── Return public API ── */
  return {
    connectionState,
    agentState,
    audioLevel,
    logs,
    startCall,
    stopCall,
    sendMessage,
    sendContext,
    toolCallCount,
    /** JARVIS MIC DEFECT FIX: human reason for the most recent failed start,
     *  set alongside ConnectionState.ERROR. Null once DISCONNECTED/CONNECTING/
     *  CONNECTED for real (cleared at the top of every fresh startCall). */
    micError,
  };
}
